/**
 * The JSON:API root path that pointers from `EntityOperationError.source.pointer`
 * are anchored to. The form's `?path=` navigation is rooted under
 * `data.attributes`, so this prefix is always stripped before mapping into a
 * form path.
 */
const ATTRIBUTES_POINTER_PREFIX = '/data/attributes';

/**
 * For entities that use the config-app backend the form data is wrapped in a
 * `domainEntityAttributes` object, but `useEntityDetails` injects that wrapper
 * itself when resolving a `?path=` value. To stay compatible with both pointer
 * styles the backend might emit ("/data/attributes/campaignNodes" or
 * "/data/attributes/domainEntityAttributes/campaignNodes"), this segment is
 * stripped opportunistically when present.
 */
const DOMAIN_ENTITY_ATTRIBUTES_SEGMENT = '/domainEntityAttributes';

/**
 * Strip `prefix` from the start of `value` if and only if the prefix is
 * followed by a `/` boundary or end-of-string. Prevents accidentally matching
 * a property whose name happens to start with the prefix (e.g. so
 * `/data/attributesX` is not treated as matching `/data/attributes`).
 */
const stripPrefixAtBoundary = (
  value: string,
  prefix: string,
): string | undefined => {
  if (!value.startsWith(prefix)) return undefined;
  const remainder = value.slice(prefix.length);
  if (remainder === '' || remainder.startsWith('/')) return remainder;
  return undefined;
};

/**
 * Decode a single JSON Pointer reference token per RFC 6901 §4: replace `~1`
 * with `/` first, then `~0` with `~`. The order matters to avoid creating
 * spurious `~1` sequences from a literal `~01`.
 */
const decodeJsonPointerSegment = (segment: string): string =>
  segment.replace(/~1/g, '/').replace(/~0/g, '~');

/**
 * Convert a JSON Pointer (RFC 6901) returned by the backend in
 * `EntityOperationError.source.pointer` into the dot-separated form path that
 * the entity-details page consumes via the `?path=` query parameter.
 *
 * Examples:
 *   "/data/attributes/title"                            -> "title"
 *   "/data/attributes/content/pages/1/header"           -> "content.pages.1.header"
 *   "/data/attributes/domainEntityAttributes/title"     -> "title"
 *
 * Returns `undefined` when the pointer is missing, doesn't anchor at
 * `/data/attributes`, or doesn't strip down to at least one navigable
 * segment. Callers should treat `undefined` as "render the error message
 * without a link".
 */
export const jsonPointerToFormPath = (
  pointer: string | undefined,
): string | undefined => {
  if (!pointer) return undefined;

  const afterAttributes = stripPrefixAtBoundary(
    pointer,
    ATTRIBUTES_POINTER_PREFIX,
  );
  if (afterAttributes === undefined) return undefined;

  // Strip the optional `domainEntityAttributes` wrapper when present so that
  // pointers from both config-app-backend and non-config-app-backend entities
  // map to the same form-path shape.
  const remainder =
    stripPrefixAtBoundary(afterAttributes, DOMAIN_ENTITY_ATTRIBUTES_SEGMENT) ??
    afterAttributes;

  if (!remainder.startsWith('/')) return undefined;

  const segments = remainder.slice(1).split('/').map(decodeJsonPointerSegment);

  if (segments.every((segment) => segment === '')) return undefined;

  return segments.join('.');
};
